import type { AuthFormData } from "./types";
import { MAX_INTERESTS } from "../../../data/interestOptions";

export const PASSWORD_MISMATCH_ERROR = "Указанные пароли не совпадают";

export const UNDERAGE_ERROR = "Мы не предоставляем доступ к сервису лицам младше 18 лет";

/**
 * Бэкенд на неподошедшую пару отвечает одной строкой «неверный логин или пароль»
 * (auth_routes.py::login, 401) — намеренно не различая «нет такого» и «пароль не
 * тот», чтобы по ответу нельзя было перебирать существующие аккаунты.
 */
const CREDENTIAL_ERROR_PATTERN = /невер|неправильн|не\s*подош/i;

export interface LoginAlert {
  /** Заголовок плашки над формой. */
  title: string;
  /** Показывать подсказку про раскладку и ссылку на сброс пароля. */
  showPasswordHint: boolean;
}

/**
 * Ошибка входа → содержимое плашки. Текст утверждён Ириной 20.08: на неподошедший
 * пароль — человеческий заголовок и подсказка, на всё остальное (429, сеть, 5xx) —
 * ровно то, что сказал сервер, без выдуманной причины.
 */
export function getLoginAlert(error: string): LoginAlert {
  if (CREDENTIAL_ERROR_PATTERN.test(error)) {
    return { title: "Не подошёл пароль", showPasswordHint: true };
  }
  return { title: error, showPasswordHint: false };
}

/** Допустимый возраст при регистрации — от 18 до 85 лет включительно (#1335). */
export const MAX_AGE = 85;
export const AGE_RANGE_ERROR = "Возраст — от 18 до 85 лет";

function toLocalIso(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Самая ранняя допустимая дата рождения: возраст ещё 85 (как age_from_birth на бэкенде).
 * Это день после даты «сегодня минус 86 лет»; если такой даты нет (29 февраля в
 * невисокосном году), берём 28 февраля, и граница — 1 марта.
 */
export function getMinBirthDate(today: Date = new Date()): string {
  const y = today.getFullYear() - (MAX_AGE + 1);
  const m = today.getMonth();
  const lastDay = new Date(y, m + 1, 0).getDate();
  const day = Math.min(today.getDate(), lastDay);
  return toLocalIso(new Date(y, m, day + 1));
}

export function getAdultMaxDate(): string {
  const today = new Date();
  const d = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  // Локальная дата, а не toISOString(): в поясах east of UTC полночь уезжала на сутки назад.
  return toLocalIso(d);
}

/** Интересы вводятся кириллицей: латиница в тегах — ошибка (YouGile IP-12). Не более MAX_INTERESTS штук. */
export function getInterestsError(value: string): string | null {
  if (!value.trim()) return null;
  if (/[a-zA-Z]/.test(value)) return "Интересы вводятся русскими буквами";
  const count = value.split(",").map((s) => s.trim()).filter(Boolean).length;
  if (count > MAX_INTERESTS) return `Можно выбрать не более ${MAX_INTERESTS} интересов`;
  return null;
}

/** null — дата валидна и пользователю есть 18; иначе текст ошибки. */
export function getBirthDateError(value: string): string | null {
  const v = value.trim();
  if (!v) return "Укажите дату рождения";
  const d = new Date(`${v}T12:00:00`);
  if (isNaN(d.getTime())) return "Укажите корректную дату рождения";
  if (v > getAdultMaxDate()) return UNDERAGE_ERROR;
  if (v < getMinBirthDate()) return AGE_RANGE_ERROR;
  return null;
}

export function createInitialFormData(): AuthFormData {
  return {
    email: "",
    authMethod: "email",
    loginPhone: "",
    password: "",
    confirmPassword: "",
    name: "",
    birthDate: "",
    birthTime: "",
    birthCity: "",
    gender: "",
    avatarUrl: "",
    photos: [],
    bio: "",
    location: "",
    interests: "",
    agreeToPrivacy: false,
    agreeToTerms: false,
    agreeToOffer: false,
    agreeToAge18: false,
    agreeToDataPolicyRead: false,
    agreeToNewsletter: false,
  };
}
